import { mapMarketingListRow } from 'containers/campaigns/marketing/lists/utils/mapMarketingListRow';
import { resolveMarketingListsOrderInfo } from 'containers/campaigns/marketing/lists/utils/resolveMarketingListsOrderInfo';
import { formatSelectionOperators } from 'domain/FilterApiWrapper';
import { getEntityCollection, getEntityCollections } from 'services/EntityCollectionsService';

function normalizeOnlySelectedType(value) {
    if (value === '1' || value === 1) {
        return 1;
    }
    if (value === '2' || value === 2) {
        return 2;
    }
    return 0;
}

function buildSelectionWhereInfo(filter = {}, selectionToken = '') {
    const normalizedToken = typeof selectionToken === 'string' ? selectionToken.trim() : '';
    if (!normalizedToken) {
        return null;
    }

    return {
        EntityShowSelected: filter?.entityShowSelected?.value || 'marketingCampaigns',
        SelectionToken: normalizedToken,
        OnlySelectedType: normalizeOnlySelectedType(filter?.OnlySelectedType?.value),
    };
}

function resolveOperatorInfo(operators) {
    const { operatorInfo } = formatSelectionOperators(operators);
    if (!operatorInfo || typeof operatorInfo !== 'object') {
        return null;
    }

    return Object.keys(operatorInfo).length > 0 ? operatorInfo : null;
}

function normalizeSelectionSearch(filter = {}) {
    const matchingName = filter?.matchingName?.value;
    if (typeof matchingName === 'string') {
        return matchingName.trim();
    }
    return '';
}

function mapEntityCollectionSelectionRow(row = {}) {
    const mappedRow = mapMarketingListRow(row);
    const id = mappedRow.id ?? row.Id ?? row.id ?? row.collectionId ?? row.CollectionId ?? null;

    return {
        ...row,
        ...mappedRow,
        Id: id,
    };
}

export default class CampaignsListsManager {
    getFilterSchema = () => [];

    getEntityCollectionsSelectionList(
        entity,
        { init, count, filter, selectionToken, operators, sortField, sortDir },
    ) {
        if (entity?.entity !== 'entityCollections') {
            return Promise.reject(new Error('Entity collections selection list is not supported'));
        }

        const normalizedSelectionToken =
            typeof selectionToken === 'string' ? selectionToken.trim() : '';
        if (!normalizedSelectionToken) {
            return Promise.resolve({ items: [], total: 0 });
        }

        const whereInfo = buildSelectionWhereInfo(filter, normalizedSelectionToken);
        if (!whereInfo) {
            return Promise.resolve({ items: [], total: 0 });
        }

        const operatorInfo = resolveOperatorInfo(operators);

        return getEntityCollections({
            search: normalizeSelectionSearch(filter),
            offset: init,
            limit: count,
            token: normalizedSelectionToken,
            whereInfo,
            operatorInfo,
            orderInfo: resolveMarketingListsOrderInfo(sortField, sortDir),
        }).then(({ items, hasMore, total }) => {
            const mappedItems = (Array.isArray(items) ? items : []).map(
                mapEntityCollectionSelectionRow,
            );
            const resolvedTotal =
                typeof total === 'number' && Number.isFinite(total)
                    ? total
                    : hasMore
                      ? init + mappedItems.length + 1
                      : init + mappedItems.length;

            return {
                items: mappedItems,
                total: resolvedTotal,
            };
        });
    }

    customInitDetail(entity, id, info, afterDetail) {
        return getEntityCollection({ collectionId: id }).then((result) => {
            const entityData = mapMarketingListRow(result || {});

            if (!afterDetail) {
                return { entityData, rawData: result };
            }

            return Promise.resolve(afterDetail(this, entityData)).then((data) => ({
                entityData: data ?? entityData,
                rawData: result,
            }));
        });
    }
}
